/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useRef } from 'react';
import { useTheme } from '../../ThemeProvider';
import { createStyles } from '../../utils';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { useOtp } from './hooks/useOtp';

const HelperText = ({ helperText }: { helperText: string }) => {
  const styles = useStyles();
  const { spacing } = useTheme();
  return (
    <span css={styles.helperTextContainer}>
      <Icon name={'error'} size={spacing.xxs} />
      <small css={styles.helperText}>{helperText}</small>
    </span>
  );
};

export interface Props {
  value: string[];
  onChange: (value: string[]) => void;
  onComplete?: () => void;
  error?: boolean;
  errorText?: string;
  testId?: string;
}

export const OTPInput = ({
  value,
  error,
  errorText,
  onChange,
  onComplete,
  testId,
}: Props) => {
  const styles = useStyles();
  const { palette } = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);

  const { activeIndex, handleOnChange, handleOnKeyDown } = useOtp({
    value,
    onChange,
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, [activeIndex]);

  const stringValue = useMemo(() => value.join('').trim(), [value]);

  useEffect(() => {
    if (onComplete && stringValue.length === value.length) {
      onComplete();
    }
  }, [stringValue, value.length, onComplete]);

  const errorStyle = {
    '.MuiOutlinedInput-root': {
      ':not(.Mui-error) > .MuiOutlinedInput-notchedOutline': {
        borderColor: palette.danger.x500,
      },
      ':not(.Mui-focused):not(.Mui-error)  > .MuiOutlinedInput-notchedOutline':
        {
          borderColor: palette.danger.x500,
        },
    },
  };

  return (
    <>
      <div css={[styles.default, { ...(error ? errorStyle : {}) }]}>
        {value.map((v, index) => (
          <Input
            type="tel"
            key={index}
            value={v}
            onChange={(e) => handleOnChange(e, index)}
            onKeyDown={(e) => handleOnKeyDown(e, index)}
            inputRef={index === activeIndex ? inputRef : null}
            placeholder="•"
            testId={testId ? `${testId}-input-${index}` : ''}
          />
        ))}
      </div>
      {error && errorText && <HelperText helperText={errorText} />}
    </>
  );
};

const useStyles = createStyles(({ spacing, typography, palette }) => ({
  default: {
    display: 'flex',
    gap: spacing.xxxs,
    '.MuiOutlinedInput-root': {
      maxWidth: `calc(${spacing.m} + ${spacing.xxxs})`,
      paddingLeft: spacing.xxxs,
    },
  },
  helperTextContainer: {
    display: 'flex',
    marginLeft: 0,
    alignItems: 'center',
    marginTop: spacing.xxxs,
  },
  helperText: {
    marginLeft: `${spacing.xxxs}`,
    color: palette.danger.default,
    ...typography.caption,
  },
}));
